import { NextPageContext } from 'next'
import { User } from '../../types'
import {
  GetSourcingAnnotationCandidates,
  SourcingAnnotationCandidatesData,
  SourcingAnnotationCandidatesError,
  Status,
  StatusesResponse,
} from '../../types/api'
import { printError } from '../log'
import { basePath, callAPI, callAPIWithNoContent, callAuthAPI } from './common'
import { callAPIRaw } from '@/components/pages/companies/common/api'

export const getSourcingAnnotationCandidates =
  async (): Promise<GetSourcingAnnotationCandidates> => {
    try {
      return await callAPI<SourcingAnnotationCandidatesData>(
        `/api/v1/sourcing_annotations/candidates`
      )
    } catch (e) {
      printError(e)
      return false as SourcingAnnotationCandidatesError
    }
  }

export const fetchStatusesSSR = async (ctx: NextPageContext) => {
  let response
  try {
    const url = urlOfStatues()
    response = await callAuthAPI<StatusesResponse>(ctx, url)
  } catch (e) {
    printError(e)
  }

  return response ? response : { statuses: [] }
}

const urlOfStatues = () => {
  return `${basePath}/statuses`
}

export const putStatus = async (
  status: Status
): Promise<{ status: Status }> => {
  const res = await callAPI<{ status: Status }>('/api/statuses', {
    method: 'PUT',
    body: JSON.stringify({
      name: status.name,
      statusId: status.id === '' ? null : status.id,
      color: status.color,
      symbol: status.symbol,
    }),
  })
  return res
}

export const putStatusOrder = async (
  user: User,
  order: { id: string; showOrder: number }[]
) => {
  await callAPI('/api/statuses/orders', {
    method: 'PUT',
    credentials: 'include',
    body: JSON.stringify({
      contractId: user.contractId,
      statuses: order,
    }),
  })
}

export const registerCompanySourcingRoute = async (
  shortname: string,
  sourcingRouteId: number
): Promise<boolean> => {
  try {
    await callAPIWithNoContent(
      `/api/v1/companies/${shortname}/sourcing_route`,
      {
        method: 'PUT',
        body: JSON.stringify({
          sourcingRouteId: sourcingRouteId,
        }),
      }
    )
    return true
  } catch (e) {
    printError(e)
    return false
  }
}

export const deleteCompanySourcingRoute = async (
  shortname: string
): Promise<boolean> => {
  try {
    await callAPIWithNoContent(
      `/api/v1/companies/${shortname}/sourcing_route`,
      {
        method: 'DELETE',
      }
    )
    return true
  } catch (e) {
    printError(e)
    return false
  }
}

export const postSourcingRoute = async (name: string): Promise<boolean> => {
  try {
    await callAPIWithNoContent(`/api/v1/sourcing_routes`, {
      method: 'POST',
      body: JSON.stringify({
        sourcingRouteName: name,
      }),
    })
    return true
  } catch (e) {
    printError(e)
    return false
  }
}

export const putSourcingRoute = async (
  id: number,
  name: string
): Promise<boolean> => {
  try {
    await callAPIWithNoContent(`/api/v1/sourcing_routes/${id}`, {
      method: 'PUT',
      body: JSON.stringify({
        sourcingRouteName: name,
      }),
    })
    return true
  } catch (e) {
    printError(e)
    return false
  }
}

export const deleteSourcingRoute = async (id: number): Promise<boolean> => {
  try {
    await callAPIWithNoContent(`/api/v1/sourcing_routes/${id}`, {
      method: 'DELETE',
    })
    return true
  } catch (e) {
    printError(e)
    return false
  }
}

export const postMyLabel = async (name: string) => {
  try {
    return callAPIRaw(`/api/v1/my_labels`, {
      method: 'POST',
      body: JSON.stringify({ name }),
    })
  } catch (e) {
    printError(e)
    return {}
  }
}

export const putMyLabel = async (id: number, name: string) => {
  try {
    return await callAPIRaw(`/api/v1/my_labels/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ name }),
    })
  } catch (e) {
    printError(e)
    return {}
  }
}

export const deleteMyLabel = (id: number) => {
  return callAPIRaw(`/api/v1/my_labels/${id}`, { method: 'DELETE' }).catch(
    printError
  )
}
